import { ReactElement, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutomationTabs } from '../Tabs';
import {
  ListContainer, RowScheduleContainer, ScheduleListContainer, TotalContainer,
} from './styles';
import { ExceptionsHeader } from '../ExceptionsHeader';
import { Button } from '~/components';

interface ScheduleListProps<Schedule, Exception> {
  exceptions: Exception[];
  schedules: Schedule[];
  renderScheduleCard: (schedule: Schedule, index: number) => ReactElement;
  renderExceptionCard: (exception: Exception, index: number) => ReactElement;
  fixed: boolean;
  addButton?:{
    onClick: (type: 'schedules' | 'exceptions') => void,
    disabled: boolean
  };
}

export function ScheduleList<Schedule, Exception>({
  exceptions,
  fixed,
  schedules,
  renderExceptionCard,
  renderScheduleCard,
  addButton,
}: Readonly<ScheduleListProps<Schedule, Exception>>): ReactElement {
  const [activeTab, setActiveTab] = useState<'schedules' | 'exceptions'>('schedules');
  const { t } = useTranslation();

  const total = activeTab === 'exceptions' ? exceptions.length : schedules.length;

  function onClickTab(tab: string): void {
    if (tab === activeTab) return;

    setActiveTab(tab as 'schedules' | 'exceptions');
  }

  function renderPairedCards(schedules: Schedule[]): ReactNode {
    return schedules
      .filter((_, i) => i % 2 === 0)
      .map((schedule, i) => {
        const realIndex = i * 2;
        return (
          <RowScheduleContainer
            key={realIndex}
            realIndex={realIndex}
          >
            {renderScheduleCard(schedule, realIndex)}
            {schedules[realIndex + 1] && renderScheduleCard(schedules[realIndex + 1], realIndex + 1)}
          </RowScheduleContainer>
        );
      });
  }

  return (
    <ScheduleListContainer fixed={fixed}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: fixed ? '100%' : undefined,
      }}
      >
        <AutomationTabs
          active={activeTab}
          onClickTab={onClickTab}
        />
        <TotalContainer fixed={fixed}>
          <div
            style={{
              fontSize: '16px',
              marginTop: '12px',
              marginLeft: '20px',
            }}
          >
            {`${t('total')}: ${total}`}
          </div>
          {addButton !== undefined && (
            <Button
              variant={addButton.disabled ? 'disabled' : 'primary'}
              style={{
                width: 'fit-content',
                padding: '6px 15px',
              }}
              onClick={() => addButton.onClick(activeTab)}
              disabled={addButton.disabled}
            >
              {t('botaoAdicionarProgramacaoExcecao', { value: activeTab === 'schedules' ? t('Programação') : t('excecao') })}
            </Button>
          )}
        </TotalContainer>
        <ListContainer fixed={fixed}>
          {activeTab === 'schedules' && renderPairedCards(schedules)}
          {activeTab === 'exceptions' && exceptions.length > 0 && (
            <>
              <ExceptionsHeader
                size="small"
              />
              {exceptions.map((exception, index) => (
                <div
                  style={{
                    marginTop: '5px',
                    marginLeft: '16px',
                    flexDirection: 'column',
                  }}
                  key={index}
                >
                  {renderExceptionCard(exception, index)}
                </div>
              ))}
            </>
          )}
        </ListContainer>
      </div>
    </ScheduleListContainer>
  );
}
